import type { AuthMethod } from '@lit-protocol/types'
import { useAtomValue } from 'jotai'
import { useCallback } from 'react'
import { GUEST_USER, userAtom } from '@/src/stores/user/atoms'
import { useRunnerSessionSigs } from './use-runner-session-sigs'

const IS_GUEST_MODE = true

/**
 * Hook for managing guest mode session signatures with Lit Protocol.
 *
 * @returns An object containing:
 *   - `initiateGuestModeSessionSigs`: Function to create new guest mode session signatures
 *   - `guestModeSessionKeyData`: Current guest mode session key data
 */
export function useGuestModeSessionSigs() {
  const { runnerSessionKeyData, initiateRunnerSessionSigs } =
    useRunnerSessionSigs()
  const user = useAtomValue(userAtom)

  /**
   * Initiates a new guest mode session by generating session signatures for a PKP using runner session signatures.
   *
   * @param pkpPublicKey - The public key of the PKP to create session signatures for
   * @param authMethod - The authentication method to use (e.g., wallet signature)
   * @returns The session signatures, session key data, and expiration
   */
  const initiateGuestModeSessionSigs = useCallback(
    async (pkpPublicKey: string, authMethod: AuthMethod) => {
      const isLoggedIn = user.id !== GUEST_USER.id
      const isLoggedInWithMPCAuthProvider =
        user.auth_provider === 'google' || user.auth_provider === 'mpc'
      if (isLoggedIn && isLoggedInWithMPCAuthProvider) {
        throw new Error('Guest mode is not available for logged in users')
      }

      return await initiateRunnerSessionSigs(
        pkpPublicKey,
        authMethod,
        IS_GUEST_MODE,
      )
    },
    [initiateRunnerSessionSigs, user.auth_provider, user.id],
  )

  return {
    initiateGuestModeSessionSigs,
    guestModeSessionKeyData: runnerSessionKeyData,
  }
}
